import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowDown, Github, Linkedin, Instagram, Shuffle } from 'lucide-react';
import { personalInfo, stats } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
};

// ─────────────────────────────────────────────────────────
//  FOTO PROFIL — Ganti array ini dengan path foto kamu
//  Simpan foto di: /public/images/
//  Format: "/images/nama-file.jpg"
//
//  Contoh jika punya 3 foto:
//  const PROFILE_PHOTOS = [
//    "/images/photo1.jpg",
//    "/images/photo2.jpg",
//    "/images/photo3.jpg",
//  ];
// ─────────────────────────────────────────────────────────
const PROFILE_PHOTOS = [
  "/images/foto (2).JPEG",
  "/images/foto (2).jpg",
  "/images/foto (3).JPEG",
];

function RotatingRing({ size = 180 }) {
  const core = Math.round(size * 0.52);
  const insets = [0, Math.round(size * 0.09), Math.round(size * 0.21)];
  return (
    <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {insets.map((inset, i) => (
        <div key={i} style={{
          position: 'absolute', inset, borderRadius: '50%',
          border: `1px ${i === 1 ? 'dashed' : 'solid'} ${['rgba(0,245,255,0.3)', 'rgba(123,47,255,0.25)', 'rgba(0,255,136,0.2)'][i]}`,
          animation: `${i % 2 === 0 ? 'rotate' : 'rotateCCW'} ${[22, 16, 30][i]}s linear infinite`,
        }}>
          <div style={{
            position: 'absolute', top: -(i === 0 ? 4 : 3), left: '50%', transform: 'translateX(-50%)',
            width: i === 0 ? 8 : 6, height: i === 0 ? 8 : 6, borderRadius: '50%',
            background: [C.cyan, C.purple, C.green][i],
            boxShadow: `0 0 10px ${[C.cyan, C.purple, C.green][i]}`,
          }} />
        </div>
      ))}
      <div style={{
        width: core, height: core, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,245,255,0.12) 0%, rgba(0,245,255,0.02) 100%)',
        border: '1.5px solid rgba(0,245,255,0.4)',
        boxShadow: '0 0 40px rgba(0,245,255,0.12), inset 0 0 30px rgba(0,245,255,0.06)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
      }}>
        <div style={{ fontSize: Math.round(size * 0.14) }}>🤖</div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: Math.max(6, Math.round(size * 0.045)), letterSpacing: 2, color: C.cyan, textAlign: 'center', textTransform: 'uppercase', lineHeight: 1.5 }}>
          AI ENG<br />& DEV
        </div>
      </div>
    </div>
  );
}

function ProfilePhoto({ src, onShuffle, hasMultiple }) {
  const [imgError, setImgError] = useState(false);
  const [shuffleAnim, setShuffleAnim] = useState(false);

  const handleShuffle = () => {
    setShuffleAnim(true);
    setTimeout(() => setShuffleAnim(false), 400);
    onShuffle();
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      {/* Spinning conic ring */}
      <div style={{
        position: 'relative',
        width: 'clamp(200px, 28vw, 300px)',
        height: 'clamp(200px, 28vw, 300px)',
        flexShrink: 0,
      }}>
        {/* Animated border */}
        <div style={{
          position: 'absolute', inset: -3, borderRadius: '50%',
          background: 'conic-gradient(from 0deg, #00f5ff, #7b2fff, #00ff88, #00f5ff)',
          animation: 'rotate 5s linear infinite',
          zIndex: 0,
        }} />
        <div style={{
          position: 'absolute', inset: -3, borderRadius: '50%',
          background: 'conic-gradient(from 0deg, #00f5ff, #7b2fff, #00ff88, #00f5ff)',
          animation: 'rotate 5s linear infinite',
          filter: 'blur(8px)', opacity: 0.5, zIndex: 0,
        }} />
        {/* White gap */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#020409', zIndex: 1 }} />

        {/* Photo */}
        <div style={{
          position: 'absolute', inset: 4, borderRadius: '50%',
          overflow: 'hidden', zIndex: 2,
          transition: 'opacity .3s',
          opacity: shuffleAnim ? 0 : 1,
        }}>
          {src && !imgError ? (
            <img
              key={src}
              src={src}
              alt="Marshall Vira Deva"
              onError={() => setImgError(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(135deg, rgba(0,245,255,0.08), rgba(123,47,255,0.12), rgba(0,255,136,0.06))',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}>
              <div style={{ fontSize: 44 }}>📸</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: C.cyan, textTransform: 'uppercase', textAlign: 'center', lineHeight: 2 }}>
                FOTO PROFIL<br />
                <span style={{ color: C.gray, fontSize: 7 }}>/public/images/<br />photo-1.jpg</span>
              </div>
            </div>
          )}
          {/* Bottom overlay */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', background: 'linear-gradient(transparent, rgba(2,4,9,0.65))', pointerEvents: 'none' }} />
        </div>

        {/* Corner accent dots */}
        {[0, 90, 180, 270].map((deg, i) => (
          <div key={i} style={{
            position: 'absolute', zIndex: 3,
            width: 8, height: 8, borderRadius: '50%',
            background: [C.cyan, C.purple, C.green, C.cyan][i],
            boxShadow: `0 0 8px ${[C.cyan, C.purple, C.green, C.cyan][i]}`,
            top: `calc(50% - 4px + ${Math.round(-51 * Math.cos(deg * Math.PI / 180))}%)`,
            left: `calc(50% - 4px + ${Math.round(51 * Math.sin(deg * Math.PI / 180))}%)`,
          }} />
        ))}
      </div>

      {/* Name card */}
      <div style={{
        textAlign: 'center', padding: '10px 20px',
        background: 'rgba(0,245,255,0.04)',
        border: '1px solid rgba(0,245,255,0.15)',
        width: '100%',
      }}>
        <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 11, fontWeight: 700, color: C.white, letterSpacing: 1 }}>
          Made Marshall Vira Deva
        </div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: C.cyan, letterSpacing: 2, marginTop: 4, textTransform: 'uppercase' }}>
          AI Engineer · Web Developer
        </div>
      </div>

      {/* Shuffle button — only if multiple photos */}
      {hasMultiple && (
        <button
          onClick={handleShuffle}
          title="Shuffle foto"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: "'Space Mono', monospace", fontSize: 8,
            letterSpacing: 2, textTransform: 'uppercase',
            color: C.gray, background: 'transparent',
            border: '1px solid rgba(0,245,255,0.15)',
            padding: '6px 14px', cursor: 'pointer',
            transition: 'all .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = C.cyan; e.currentTarget.style.borderColor = 'rgba(0,245,255,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = C.gray; e.currentTarget.style.borderColor = 'rgba(0,245,255,0.15)'; }}
        >
          <Shuffle size={10} /> Shuffle foto
        </button>
      )}
    </div>
  );
}

export default function Hero() {
  const titleRef = useRef(null);
  const validPhotos = PROFILE_PHOTOS.filter(Boolean);
  const [photoIdx, setPhotoIdx] = useState(0);

  const shufflePhoto = useCallback(() => {
    if (validPhotos.length <= 1) return;
    setPhotoIdx(prev => {
      let next;
      do { next = Math.floor(Math.random() * validPhotos.length); } while (next === prev && validPhotos.length > 1);
      return next;
    });
  }, [validPhotos.length]);

  // Auto-shuffle every 5 seconds if multiple photos
  useEffect(() => {
    if (validPhotos.length <= 1) return;
    const timer = setInterval(shufflePhoto, 5000);
    return () => clearInterval(timer);
  }, [shufflePhoto, validPhotos.length]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const chars = '!@#$%^&*01アイウエオカキクケコ';
    let interval;
    const glitch = () => {
      const original = el.dataset.original || el.innerText;
      el.dataset.original = original;
      let i = 0;
      interval = setInterval(() => {
        el.innerText = original.split('').map((c, idx) =>
          idx < i ? c : (Math.random() > 0.7 ? chars[Math.floor(Math.random() * chars.length)] : c)
        ).join('');
        i++;
        if (i > original.length) { el.innerText = original; clearInterval(interval); }
      }, 28);
    };
    const timer = setTimeout(glitch, 800);
    const repeat = setInterval(glitch, 8000);
    return () => { clearTimeout(timer); clearInterval(repeat); clearInterval(interval); };
  }, []);

  const currentPhoto = validPhotos.length > 0 ? validPhotos[photoIdx] : null;

  return (
    <section id="hero" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      padding: '120px 60px 80px', position: 'relative', overflow: 'hidden', zIndex: 2,
    }}>
      {/* Orbs */}
      <div className="orb" style={{ width: 550, height: 550, background: 'rgba(0,245,255,0.05)', top: -120, right: -80, animation: 'orbFloat 9s ease-in-out infinite' }} />
      <div className="orb" style={{ width: 400, height: 400, background: 'rgba(123,47,255,0.06)', bottom: 0, left: -100, animation: 'orbFloat 12s ease-in-out infinite reverse' }} />
      {/* Scan line */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: 'linear-gradient(transparent, rgba(0,245,255,0.08), transparent)', animation: 'scanLine 6s linear infinite' }} />
      </div>

      {/* ── HERO LAYOUT ── */}
      <div className="hero-grid" style={{ width: '100%', position: 'relative', zIndex: 2 }}>

        {/* LEFT: Text */}
        <div className="hero-text">
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: "'Space Mono', monospace", fontSize: 10,
            letterSpacing: 3, textTransform: 'uppercase',
            color: C.green, border: '1px solid rgba(0,255,136,0.3)',
            padding: '6px 16px', marginBottom: 24, animation: 'fadeUp .7s ease both',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.green, animation: 'pulse 1.4s ease-in-out infinite' }} />
            {personalInfo.status}
          </div>

          <h1 style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: 'clamp(36px, 5.5vw, 82px)',
            fontWeight: 900, lineHeight: 1.0, letterSpacing: -2,
            animation: 'fadeUp .7s ease .1s both', margin: '0 0 8px',
          }}>
            <span ref={titleRef} style={{ display: 'block', color: C.white }}>MARSHALL</span>
            <span style={{
              display: 'block',
              background: `linear-gradient(90deg, ${C.cyan}, ${C.purple}, ${C.green}, ${C.cyan})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text', backgroundSize: '200% auto',
              animation: 'gradShift 4s linear infinite',
            }}>VIRA DEVA</span>
          </h1>

          <div style={{
            fontFamily: "'Space Mono', monospace", fontSize: 10,
            letterSpacing: 3, textTransform: 'uppercase',
            color: C.gray, margin: '18px 0', animation: 'fadeUp .7s ease .2s both',
          }}>
            {personalInfo.title}&nbsp;&nbsp;<span style={{ color: C.cyan }}>// Bali, ID</span>
          </div>

          <p style={{
            fontSize: 15, lineHeight: 1.9, color: 'rgba(232,244,248,0.62)',
            maxWidth: 420, marginBottom: 32, animation: 'fadeUp .7s ease .3s both',
          }}>
            {personalInfo.tagline}{' '}
            Master of IS @ <span style={{ color: C.cyan }}>Telkom University</span>.{' '}
            <span style={{ color: C.green }}>Web Dev & Digital Marketing</span> @ Dewanta Villas.
          </p>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', animation: 'fadeUp .7s ease .4s both', marginBottom: 40 }}>
            <a href="#projects"
              onClick={e => { e.preventDefault(); document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{
                padding: '11px 22px',
                background: `linear-gradient(135deg, ${C.cyan}, #00c4cc)`,
                color: '#020409', fontFamily: "'Space Mono', monospace",
                fontSize: 9, letterSpacing: 2, textTransform: 'uppercase',
                fontWeight: 700, cursor: 'pointer', display: 'inline-flex',
                alignItems: 'center', gap: 8, border: 'none',
                transition: 'all .3s', textDecoration: 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 28px rgba(0,245,255,0.5)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              View Projects <ArrowDown size={12} />
            </a>
            {[
              { href: personalInfo.linkedin, label: 'LinkedIn', icon: <Linkedin size={12} />, hc: C.cyan },
              { href: personalInfo.github,   label: 'GitHub',   icon: <Github size={12} />,   hc: C.cyan },
              { href: personalInfo.instagram,label: 'Instagram',icon: <Instagram size={12} />, hc: '#E1306C' },
            ].map((btn, i) => (
              <a key={i} href={btn.href} target="_blank" rel="noreferrer"
                style={{
                  padding: '11px 18px', border: '1px solid rgba(0,245,255,0.2)',
                  color: C.white, fontFamily: "'Space Mono', monospace",
                  fontSize: 9, letterSpacing: 2, textTransform: 'uppercase',
                  background: 'transparent', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'all .3s', textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = btn.hc; e.currentTarget.style.color = btn.hc; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,245,255,0.2)'; e.currentTarget.style.color = C.white; }}
              >
                {btn.icon} {btn.label}
              </a>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', animation: 'fadeUp .7s ease .5s both', borderTop: '1px solid rgba(0,245,255,0.08)', paddingTop: 22 }}>
            {stats.map((s, i) => (
              <div key={i}>
                <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 'clamp(20px, 2.5vw, 30px)', fontWeight: 700, color: C.cyan, lineHeight: 1 }}>{s.num}</div>
                <div style={{ fontSize: 9, letterSpacing: 2, color: C.gray, textTransform: 'uppercase', marginTop: 4, fontFamily: "'Space Mono', monospace" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CENTER: Photo */}
        <div className="hero-photo" style={{ display: 'flex', justifyContent: 'center', animation: 'fadeIn 1s ease .3s both' }}>
          <ProfilePhoto
            src={currentPhoto}
            onShuffle={shufflePhoto}
            hasMultiple={validPhotos.length > 1}
          />
        </div>

        {/* RIGHT: Robot + data panel */}
        <div className="hero-robot" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, animation: 'fadeIn 1s ease .6s both' }}>
          <div className="ring-wrap" style={{ lineHeight: 0 }}>
            {/* ukuran ring: 180px desktop, mengecil otomatis di tablet via CSS */}
            <RotatingRing size={180} />
          </div>
          <div className="data-panel" style={{
            fontFamily: "'Space Mono', monospace",
            background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(0,245,255,0.12)',
            padding: '14px 18px', width: '100%', maxWidth: 200,
          }}>
            {[
              { label: 'STATUS', value: 'ONLINE',     color: C.green  },
              { label: 'ROLE',   value: 'WEB DEV',    color: C.cyan   },
              { label: 'CITY',   value: 'BALI, ID',   color: C.white  },
              { label: 'GPA',    value: '3.86 / 4.0', color: C.purple },
            ].map((row, i, arr) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between',
                marginBottom: i < arr.length - 1 ? 9 : 0,
                paddingBottom: i < arr.length - 1 ? 9 : 0,
                borderBottom: i < arr.length - 1 ? '1px solid rgba(0,245,255,0.06)' : 'none',
              }}>
                <span style={{ fontSize: 7, letterSpacing: 2, color: C.gray }}>{row.label}</span>
                <span style={{ fontSize: 8, letterSpacing: 1, color: row.color, fontWeight: 600 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Email sidebar — hide on small screens */}
      <div className="email-sidebar" style={{
        position: 'absolute', right: 20, bottom: 50,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        animation: 'fadeIn 1s ease 1s both', zIndex: 3,
      }}>
        <a href={`mailto:${personalInfo.email}`}
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 3, color: C.gray, textDecoration: 'none', writingMode: 'vertical-rl', transition: 'color .2s' }}
          onMouseEnter={e => e.target.style.color = C.cyan}
          onMouseLeave={e => e.target.style.color = C.gray}
        >
          {personalInfo.email}
        </a>
        <div style={{ width: 1, height: 50, background: 'linear-gradient(to bottom, rgba(0,245,255,0.4), transparent)' }} />
      </div>

      <style>{`
        /* ── Desktop: 3 kolom ── */
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          grid-template-areas: "text photo robot";
          align-items: center;
          gap: 40px;
        }
        .hero-text  { grid-area: text; }
        .hero-photo { grid-area: photo; }
        .hero-robot { grid-area: robot; }

        /* ── Tablet (≤1024px): foto atas, teks bawah, robot bawah sendiri ── */
        @media (max-width: 1024px) {
          .hero-grid {
            grid-template-columns: 1fr;
            grid-template-areas:
              "photo"
              "text"
              "robot";
            justify-items: center;
            gap: 28px;
          }
          .hero-robot {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 24px;
            width: 100%;
            max-width: 480px;
          }
          .hero-robot .ring-wrap { flex-shrink: 0; }
          .hero-robot .data-panel { flex: 1; min-width: 0; }
          .hero-text  { text-align: center; max-width: 600px; width: 100%; }
          .hero-text p { margin-left: auto; margin-right: auto; }
          .hero-text > div:first-child { justify-content: center; }
          #hero { padding: 100px 40px 60px; }
        }

        /* ── Mobile (≤640px) ── */
        @media (max-width: 640px) {
          #hero { padding: 90px 20px 50px; }
          .hero-grid { gap: 20px; }
          .email-sidebar { display: none; }
          .hero-robot {
            flex-direction: column !important;
            gap: 16px !important;
          }
          .hero-robot .data-panel { width: 100%; max-width: 260px; }
        }

        /* ── Small mobile (≤400px) ── */
        @media (max-width: 400px) {
          #hero { padding: 80px 16px 40px; }
          .hero-robot .ring-wrap { transform: scale(0.82); transform-origin: center; }
        }
      `}</style>
    </section>
  );
}
